import React, { useId, useMemo } from 'react';
import PropTypes from 'prop-types';
import { MultiSelect } from '~/shadcn/components/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shadcn/components/ui/Select';
import { Loader2 } from 'lucide-react';
import { useFacilitiesQuery } from 'shared/hooks/warehouse/useFacilitiesQuery';

/**
 * Renders a facility picker select component.
 *
 * @param {Object} props Component props.
 * @param {string|number|Object|Array} [props.value] Selected facility ID(s) or facility object(s).
 * @param {Function} [props.onValueChange] Change handler invoked with the selected facility object(s).
 * @param {string} [props.label='Facility'] Visible label rendered above the picker.
 * @param {boolean} [props.showLabel=true] Shows the label when true, hides it when false.
 * @param {boolean} [props.multiSelect=false] Enables multi-select mode when true.
 * @param {boolean} [props.includeClusterInLabel=false] Appends the cluster name to facility labels.
 * @param {boolean} [props.excludeNonPackingFacilities=false] Filters out facilities where packingFacility is false.
 * @returns {JSX.Element} Facility picker component.
 */
export function FacilityPickerV2({
    value,
    onValueChange,
    label = 'Facility',
    showLabel = true,
    multiSelect = false,
    includeClusterInLabel = false,
    excludeNonPackingFacilities = false,
}) {
    const labelId = useId();
    const { data: facilities = [], isLoading } = useFacilitiesQuery();

    // Extract ID from value (handles both ID and facility object)
    const extractId = (val) => {
        if (val == null) return null;
        if (typeof val === 'object' && val.id != null) {
            return String(val.id);
        }
        return String(val);
    };

    // Format cluster name for display
    const formatClusterName = (cluster) => {
        if (!cluster) return '';
        if (cluster === 'losangeles') return 'Los Angeles';
        if (cluster === 'sanantonio') return 'San Antonio';
        return cluster.charAt(0).toUpperCase() + cluster.slice(1);
    };

    // Build label for a facility option
    const buildLabel = (facility) => {
        if (includeClusterInLabel && facility.cluster) {
            return `${facility.name} - ${formatClusterName(facility.cluster)}`;
        }
        return facility.name;
    };

    // Filter and build options from facilities
    const options = useMemo(() => {
        let filteredFacilities = facilities;

        if (excludeNonPackingFacilities) {
            filteredFacilities = facilities.filter((facility) => facility.packingFacility === true);
        }

        const facilityOptions = filteredFacilities.map((facility) => ({
            value: String(facility.id),
            label: buildLabel(facility),
        }));

        return facilityOptions;
    }, [facilities, includeClusterInLabel, excludeNonPackingFacilities]);

    // Lookup map for quick access
    const facilitiesById = useMemo(() => {
        const map = new Map();
        facilities.forEach((facility) => map.set(String(facility.id), facility));
        return map;
    }, [facilities]);

    // Derive selected value directly from prop
    const selectedValue = useMemo(() => {
        if (multiSelect) {
            if (Array.isArray(value)) {
                return value.map(extractId).filter(Boolean);
            }
            const id = extractId(value);
            return id ? [id] : [];
        }
        return extractId(value) ?? '';
    }, [value, multiSelect]);

    // Handle single select change
    const handleSingleValueChange = (newValue) => {
        const facility = facilitiesById.get(newValue);
        if (facility && onValueChange) {
            onValueChange(facility);
        }
    };

    // Handle multi-select change
    const handleMultiValueChange = (newValues) => {
        if (onValueChange) {
            const selectedFacilities = newValues.map((val) => facilitiesById.get(val)).filter(Boolean);
            onValueChange(selectedFacilities);
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col gap-2">
                {showLabel && label && (
                    <span
                        id={labelId}
                        className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                        {label}
                    </span>
                )}
                <div className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading facilities…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {showLabel && label && (
                <span
                    id={labelId}
                    className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                    {label}
                </span>
            )}
            {multiSelect ? (
                <MultiSelect
                    aria-labelledby={labelId}
                    options={options}
                    defaultValue={selectedValue}
                    onValueChange={handleMultiValueChange}
                    placeholder="Select facilities"
                />
            ) : (
                <Select
                    value={selectedValue}
                    onValueChange={handleSingleValueChange}
                    aria-labelledby={labelId}
                    disabled={!options.length}
                >
                    <SelectTrigger className="w-full justify-between">
                        <SelectValue placeholder="Select facility" />
                    </SelectTrigger>
                    <SelectContent>
                        {options.length > 0 ? (
                            options.map(({ value: optionValue, label: optionLabel }) => (
                                <SelectItem key={optionValue} value={optionValue}>
                                    {optionLabel}
                                </SelectItem>
                            ))
                        ) : (
                            <SelectItem value="__no-facilities" disabled>
                                No facilities available
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}

FacilityPickerV2.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
    ]),
    onValueChange: PropTypes.func,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    showLabel: PropTypes.bool,
    multiSelect: PropTypes.bool,
    includeClusterInLabel: PropTypes.bool,
    excludeNonPackingFacilities: PropTypes.bool,
};

FacilityPickerV2.defaultProps = {
    value: undefined,
    onValueChange: undefined,
    label: 'Facility',
    showLabel: true,
    multiSelect: false,
    includeClusterInLabel: false,
    excludeNonPackingFacilities: false,
};

FacilityPickerV2.displayName = 'FacilityPickerV2';
